/**
 * auth-hooks.ts — Convenience selectors over the Zustand auth
 * store. Components consume these instead of reaching into the
 * raw store so back-compat shims (e.g. defaulting `roleKeys` to
 * [] for sessions persisted before Phase 11f) live in one place.
 */

import { useAuthStore } from '@/lib/auth-store';
import { isPlatformUser, type AuthenticatedUser, type SystemRoleKey } from '@/types/auth';

/**
 * True when the active user is operating in read-only mode (every
 * role they hold is readOnly). Components should hide write
 * affordances when this returns true — but the backend remains the
 * authority via PolicyService.
 */
export function useReadOnly(): boolean {
  return useAuthStore((s) => s.user?.isReadOnly ?? false);
}

/**
 * True when the user holds at least one role with isManager=true.
 * Drives "My team" entry visibility in the sidebar + manager-only
 * widgets.
 */
export function useIsManager(): boolean {
  return useAuthStore((s) => s.user?.isManager ?? false);
}

/**
 * Returns the user's stable role keys. Safe-default to [] for
 * sessions persisted before Phase 11f's auth-response extension.
 */
export function useRoleKeys(): SystemRoleKey[] {
  return useAuthStore((s) => (s.user?.roleKeys ?? []) as SystemRoleKey[]);
}

/**
 * True when the user holds any of the supplied role keys (OR
 * semantics). Used to gate navigation links + page-level UI.
 */
export function useHasRole(...keys: SystemRoleKey[]): boolean {
  return useAuthStore((s) => {
    const held = s.user?.roleKeys ?? [];
    if (s.user && isPlatformUser(s.user)) return true;
    return keys.some((k) => held.includes(k));
  });
}

/**
 * Shortcut for the common pattern of "render this when the user
 * can write, fall back to a label when they can't".
 */
export function useCanWrite(): boolean {
  return !useReadOnly();
}

/**
 * Returns the AuthenticatedUser snapshot, or null when logged out.
 */
export function useCurrentUser(): AuthenticatedUser | null {
  return useAuthStore((s) => s.user);
}
